// ═══════════════════════════════════════════════════════════════
// Map Adapter — Bridge between the store (percentage-based) and
// The Canvas MapEngine (world-space pixels).
// ═══════════════════════════════════════════════════════════════

import type { Table, ZoneConfig } from "../store/types";
import type { MapConnection, MapLayout, MapTable, MapZone } from "./map-engine/types";
import { brandZoneColor } from "./zone-brand-colors";

// World dimensions — the "canvas world" in which zones and tables live.
// Percentage positions from the store are scaled into this space.
export const WORLD_W = 1300;
export const WORLD_H = 960;

/** Legal world extents for MapEngine — 2× headroom on every side so real
 *  restaurants (some seed tables sit at x≈128%) aren't filtered out, while
 *  stray items created when the camera was panned far from origin (x > 200%)
 *  are treated as zombies by fitToContent and clamped away by addTable/addZone. */
export const WORLD_BOUNDS = {
  maxX: 2 * WORLD_W,
  maxY: 2 * WORLD_H,
  minX: -WORLD_W,
  minY: -WORLD_H,
};

// ── Store → Engine ────────────────────────────────────────────

function pctToWorld(pct: number, axis: "x" | "y"): number {
  return Math.round((pct / 100) * (axis === "x" ? WORLD_W : WORLD_H));
}

// Generic palette for structural blocks (the BARRA mueble, kitchen,
// restroom, etc.). The Claude Design handoff styles these as a
// neutral grey strip — not a wash of the zone's brand colour, since
// the block represents furniture rather than a seating area.
const BLOCK_FILL = "#F0EDE5";
const BLOCK_BORDER = "rgba(33,29,26,0.09)";
const BLOCK_LABEL = "#8a8175";

export function storeZoneToMap(z: ZoneConfig): MapZone {
  // Structural blocks (``isBlock=true``) bypass the brand palette
  // and use the neutral grey strip from the design's bar-counter
  // recipe. Otherwise we override the backend colours with the brand
  // palette for the five named zones (Comedor, Bar, Barra, Terraza,
  // Patio); zones outside that set keep whatever the admin saved.
  // The brand colours arrive pre-mixed so the renderer can paint
  // them as solids without additional alpha attenuation.
  const brand = z.isBlock ? null : brandZoneColor(z.name);
  const useBlockPalette = z.isBlock === true;
  return {
    border: useBlockPalette ? BLOCK_BORDER : (brand?.border ?? z.borderColor),
    color: useBlockPalette ? BLOCK_FILL : (brand?.fill ?? z.color),
    h: pctToWorld(z.height, "y"),
    id: z.id,
    isBlock: z.isBlock,
    isAvailable: z.isAvailable,
    walkInOnly: z.reservationMode === "walk_in_only",
    label: z.name.toUpperCase(),
    labelColor: useBlockPalette ? BLOCK_LABEL : (brand?.label ?? z.borderColor),
    preMixedColors: brand !== null || useBlockPalette,
    w: pctToWorld(z.width, "x"),
    x: pctToWorld(z.x, "x"),
    y: pctToWorld(z.y, "y"),
  };
}

/**
 * Engine-space size for a table with the given capacity and shape. Shared
 * between ``storeTableToMap`` and the in-canvas creation flow so a new
 * table doesn't render at a different size than the seeded ones.
 */
export function tableEngineSize(capacity: number, shape: Table["shape"]): { w: number; h: number } {
  const size = capacity >= 8 ? 100 : capacity >= 6 ? 88 : 76;
  return {
    w: shape === "round" || capacity < 6 ? size : size + 10,
    h: size,
  };
}

export function storeTableToMap(t: Table, zone?: ZoneConfig | null): MapTable {
  const { w, h } = tableEngineSize(t.maxCapacity, t.shape);
  // A table is "unavailable" when itself is disabled OR when its zone is.
  // Either way, it cannot accept new bookings/walk-ins, and the canvas paints
  // it greyed out so operators see the state at a glance.
  const isUnavailable = t.isAvailable === false || zone?.isAvailable === false;
  return {
    id: t.id,
    x: pctToWorld(t.x, "x"),
    y: pctToWorld(t.y, "y"),
    shape: t.shape === "round" ? "circle" : "square",
    w,
    h,
    capacity: t.maxCapacity,
    status: isUnavailable ? "unavailable" : "available",
    label: t.name.replace(/\s*-\s*.*$/, ""), // "M5 - Privado" → "M5"
    icon: featureToIcon(t.features),
    timer: 0,
    noShowTimer: -1,
    zone: t.zoneId || null,
  };
}

function featureToIcon(features: string[]): MapTable["icon"] {
  if (features.includes("outdoor_view")) {
    return "outdoor";
  }
  if (features.includes("pet_friendly")) {
    return "outdoor";
  }
  if (features.includes("accessible")) {
    return "disabled";
  }
  if (features.includes("high_chair")) {
    return "baby";
  }
  if (features.includes("quiet_zone")) {
    return "sound";
  }
  return "none";
}

export function storeConnectionsToMap(tables: Table[]): MapConnection[] {
  const connections: MapConnection[] = [];
  const seen = new Set<string>();
  for (const t of tables) {
    for (const nId of t.neighbors) {
      const key = t.id < nId ? `${t.id}-${nId}` : `${nId}-${t.id}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      connections.push([t.id, nId]);
    }
  }
  return connections;
}

/** Convert full store state to a MapLayout */
export function buildLayout(storeTables: Table[], storeZones: ZoneConfig[]): MapLayout {
  const zoneById = new Map(storeZones.map((z) => [z.id, z]));
  return {
    connections: storeConnectionsToMap(storeTables),
    fixedZones: [],
    tables: storeTables.map((t) => storeTableToMap(t, zoneById.get(t.zoneId) ?? null)),
    zones: storeZones.map(storeZoneToMap),
  };
}

// ── Engine → Store ────────────────────────────────────────────

export function worldToPct(px: number, axis: "x" | "y"): number {
  return Math.round((px / (axis === "x" ? WORLD_W : WORLD_H)) * 100 * 10) / 10;
}

export function mapTableToStore(mt: MapTable, original: Table): Partial<Table> {
  return {
    x: worldToPct(mt.x, "x"),
    y: worldToPct(mt.y, "y"),
    name: original.name, // Preserve full name
    maxCapacity: mt.capacity,
    minCapacity: original.minCapacity, // Engine doesn't track min — preserve
    shape: mt.shape === "circle" ? "round" : "square",
    zoneId: mt.zone || "",
  };
}

export function mapZoneToStore(mz: MapZone): Partial<ZoneConfig> {
  // Brand zones (and structural blocks) carry overridden colours +
  // an uppercased label that the renderer painted on top of the
  // backend's stored values. We must NOT round-trip those overrides
  // back to the API or we'd permanently clobber the operator's
  // configured colour / name. Geometry-only patch in that case.
  const isOverridden = mz.preMixedColors === true;
  return {
    height: worldToPct(mz.h, "y"),
    isBlock: mz.isBlock,
    ...(mz.isAvailable !== undefined && { isAvailable: mz.isAvailable }),
    ...(isOverridden ? {} : { borderColor: mz.border, color: mz.color, name: mz.label }),
    width: worldToPct(mz.w, "x"),
    x: worldToPct(mz.x, "x"),
    y: worldToPct(mz.y, "y"),
  };
}
